import { useEffect, useState } from "react";
import { useCart } from "@/context/CartContext";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { FlaskConical, Check } from "lucide-react";

// Optional reconstitution-solution add-ons. Names and prices are owner-specified;
// product records are reused from the catalog (never duplicated).
const RECON_ADDONS = [
    { productId: "bac-water-3ml", variantLabel: "3 mL", displayName: "Reconstitution Solution", displayPrice: "$5.99" },
    { productId: "bw-water-h", variantLabel: "30 mL", displayName: "Reconstitution Solution H — H Brand", displayPrice: "$25.99" },
    { productId: "bw-water-ebac", variantLabel: "30 mL", displayName: "Reconstitution Solution- Ebac", displayPrice: "$19.99" },
];

const GROUPS = [
    { group: "Reconstitution Solution — For Most Research Compounds", subtitle: "For Most Research Compounds", items: RECON_ADDONS.slice(0, 2) },
    { group: "Reconstitution Solution- Ebac — Alternative Option", subtitle: "Alternative Reconstitution Solution", items: RECON_ADDONS.slice(2) },
];

export default function ReconAddons({ className = "" }) {
    const { add, has } = useCart();
    const [addonData, setAddonData] = useState({});
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        Promise.all(RECON_ADDONS.map((a) => api.get(`/materials/${a.productId}`).then((r) => r.data)))
            .then((materials) => setAddonData(Object.fromEntries(materials.map((m) => [m.id, m]))))
            .catch(() => setFailed(true));
    }, []);

    const addAddon = (addon) => {
        const material = addonData[addon.productId];
        const variant = material?.variants?.find((v) => v.label === addon.variantLabel);
        if (!material || !variant) {
            toast.error("Could not add item", { description: "This option is unavailable right now — your cart is unaffected." });
            return;
        }
        add(material, variant);
        toast.success("Added to your order", { description: `${addon.displayName} — ${addon.displayPrice}` });
    };

    return (
        <div className={className} data-testid="recon-addons">
            <h3 className="font-serif text-2xl font-light text-brand leading-snug">Add Reconstitution Solution to Your Order</h3>
            <p className="mt-3 text-xs leading-relaxed text-slate-600">
                Lyophilized research materials require reconstitution before laboratory use.{" "}
                <strong className="font-semibold text-brand">Reconstitution solution is not included with any product</strong>{" "}
                and is sold separately — we highly recommend adding it if you do not already have it on hand.
            </p>
            {failed ? (
                <p className="mt-4 text-xs text-red-700" data-testid="recon-addons-error">
                    Reconstitution solutions could not be loaded right now. Your cart is unaffected.
                </p>
            ) : (
                <div className="mt-5">
                    {GROUPS.map((g) => (
                        <div key={g.group} className="mt-5 first:mt-0">
                            <p className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">{g.group}</p>
                            <div className="mt-3 space-y-3">
                                {g.items.map((a) => {
                                    const material = addonData[a.productId];
                                    const variant = material?.variants?.find((v) => v.label === a.variantLabel);
                                    const inCart = variant ? has(a.productId, variant) : false;
                                    const unavailable = !variant || variant.available === false || (variant.inventory != null && variant.inventory <= 0);
                                    return (
                                        <div
                                            key={a.productId}
                                            className="flex flex-wrap items-center gap-x-4 gap-y-3 border border-stone-200 bg-white rounded-sm p-4"
                                            data-testid={`recon-addon-${a.productId}`}
                                        >
                                            {material?.image_url ? (
                                                <img
                                                    src={material.image_url}
                                                    alt={a.displayName}
                                                    className="h-16 w-12 object-cover border border-stone-200 shrink-0"
                                                />
                                            ) : (
                                                <span className="h-16 w-12 shrink-0 bg-surface2 border border-stone-200 flex items-center justify-center">
                                                    <FlaskConical className="h-5 w-5 text-gold/60" strokeWidth={1.25} />
                                                </span>
                                            )}
                                            <div className="flex-1 min-w-[9rem]">
                                                <p className="text-sm font-bold text-brand">{a.displayName}</p>
                                                <p className="mt-1 text-[0.55rem] uppercase tracking-[0.18em] text-slate-400 leading-relaxed">{g.subtitle}</p>
                                            </div>
                                            <span className="font-serif text-lg text-wine shrink-0 ml-auto sm:ml-0" data-testid={`recon-addon-price-${a.productId}`}>
                                                {a.displayPrice}
                                            </span>
                                            {inCart ? (
                                                <span
                                                    className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 px-4 py-2 text-[0.6rem] uppercase tracking-[0.15em] font-semibold text-gold shrink-0"
                                                    data-testid={`recon-addon-added-${a.productId}`}
                                                >
                                                    <Check className="h-3.5 w-3.5" /> In Cart
                                                </span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => addAddon(a)}
                                                    disabled={unavailable}
                                                    data-testid={`recon-addon-add-${a.productId}`}
                                                    className="shrink-0 rounded-full border border-wine/50 px-4 py-2 text-[0.65rem] uppercase tracking-[0.15em] font-semibold text-wine hover:bg-wine hover:text-ivory transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    {unavailable ? "Unavailable" : "+ Add"}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="mt-6 flex items-start gap-3 border border-stone-200 bg-white rounded-sm p-4" data-testid="recon-addons-disclaimer">
                <span className="h-8 w-8 shrink-0 rounded-full bg-surface2 border border-gold/25 flex items-center justify-center">
                    <FlaskConical className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                </span>
                <p className="text-[0.65rem] leading-relaxed text-slate-500">
                    GrayHaven Research products are offered for laboratory research use only. No dosing,
                    administration, or usage guidance is provided.
                </p>
            </div>
        </div>
    );
}
